import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Modal, Input, Tabs } from 'antd';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeStats, NdoeStatCard, NdoeActionButton,
  NdoeLoading, NdoeError, NdoeEmpty, NdoeImage, NdoeRecherche,
  filtreTexte, optionsDe, compterActifs,
} from '../../components/ndoe';
import { chargerListe } from '../../components/ndoe/chargement';
import { buildWaLink } from '../../utils/whatsapp';
import useMontantsEchange from '../../hooks/useMontantsEchange';
import './centreEchanges.css';

/*
  Centre des échanges — un seul endroit pour un cycle qui vivait éclaté sur
  trois écrans sans lien : les demandes (/echangeForm), la sélection du
  remplacement (/echange/:id) et la liste (/echange). On ne voyait nulle part
  « où en sont mes échanges ».

  Trois temps, trois onglets, dans l'ordre du cycle :
    À traiter  — une cliente veut échanger, personne n'a encore agi
    En cours   — le livreur a la mission, elle n'est pas exécutée
    Historique — dénoué (terminé ou annulé), avec l'écart de prix s'il y en a eu

  L'onglet « En cours » est celui qui manquait vraiment : c'est la supervision.
  Sans lui, un échange confié à un livreur disparaissait de la vue du bureau
  jusqu'à son exécution.

  ── 12/08/2026 — LE CENTRE AGIT, IL NE FAIT PLUS QUE REGARDER ──────────────
  Trois actions demandées par la propriétaire, chacune sans quitter l'écran :
  annuler une demande (motif obligatoire, admin), écrire à la cliente sur les
  trois onglets, relancer un livreur qui traîne.

  ── ET IL NE CONFOND PLUS PANNE ET ABSENCE ─────────────────────────────────
  Les trois sources se chargeaient avec `.catch(() => ({ data: [] }))` : un
  serveur muet donnait « Aucune demande — rien en attente de traitement », ce
  qui se lit comme un fait de gestion. Chaque onglet porte maintenant SA panne,
  et les compteurs se taisent au lieu d'afficher zéro.
*/

const jours = (d) => {
  if (!d) return null;
  const ms = Date.now() - new Date(d).getTime();
  return Math.max(0, Math.floor(ms / 86400000));
};

const paireLisible = (produit, taille) => [produit, taille ? `P${taille}` : null].filter(Boolean).join(' · ');

/** Le bouton WhatsApp, partout pareil — absent si on n'a pas de numéro. */
const LienWhatsApp = ({ tel, nom }) => (tel ? (
  <a
    className="ce-wa"
    href={buildWaLink(tel, `Bonjour Madame ${nom || ''}`)}
    target="_blank"
    rel="noreferrer"
  >
    WhatsApp
  </a>
) : null);

const CentreEchanges = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const navigate = useNavigate();
  const role = useSelector((s) => s.user?.currentUser?.role);
  const estAdmin = role === 'admin';
  // La relance est un acte de supervision : le serveur la réserve à
  // admin + secrétaire. On cache le bouton pour les autres plutôt que de leur
  // offrir un 403.
  const peutRelancer = estAdmin || role === 'secretaire';
  // Meme regle que le reste du parcours d'echange : admin, ou permission
  // retours.voir_montants. Le SERVEUR applique la meme cle avant de mettre le
  // moindre prix dans la reponse — l'ecran ne fait pas rideau devant une charge
  // utile qui contiendrait les montants de toute facon.
  const peutVoirMontants = useMontantsEchange();

  const [onglet, setOnglet] = useState('demandes');
  const [demandes, setDemandes] = useState([]);
  const [encours, setEncours] = useState([]);
  const [histo, setHisto] = useState([]);
  // Une panne par source : un historique injoignable ne doit pas faire croire
  // qu'il n'y a aucune demande à traiter.
  const [errDemandes, setErrDemandes] = useState(null);
  const [errEncours, setErrEncours] = useState(null);
  const [errHisto, setErrHisto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [livreur, setLivreur] = useState(undefined);

  // Annulation d'une demande : motif obligatoire, comme sur l'écran d'origine.
  const [annulCible, setAnnulCible] = useState(null);
  const [motif, setMotif] = useState('');
  const [annulEnCours, setAnnulEnCours] = useState(false);
  // Relance en cours, par échange (le bouton se verrouille tout seul).
  const [relanceId, setRelanceId] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    // Les trois sources en parallèle : le centre doit s'ouvrir d'un bloc, pas
    // se remplir onglet par onglet.
    const [d, e, h] = await Promise.all([
      chargerListe(axios.get(`${DOMAIN}/api/livraison/echanges-demandes`), 'les demandes d’échange'),
      chargerListe(axios.get(`${DOMAIN}/api/livraison/echanges-a-livrer`), 'les échanges en cours'),
      chargerListe(axios.get(`${DOMAIN}/api/livraison/echanges-historique`, { params: { limite: 100 } }), 'l’historique des échanges'),
    ]);
    setDemandes(d.liste); setErrDemandes(d.erreur);
    setEncours(e.liste); setErrEncours(e.erreur);
    setHisto(h.liste); setErrHisto(h.erreur);
    setLoading(false);
  }, [DOMAIN]);

  useEffect(() => { charger(); }, [charger]);

  // Le plus vieil échange en cours : c'est l'indicateur qui doit inquiéter.
  const plusVieux = useMemo(
    () => encours.reduce((m, x) => Math.max(m, jours(x.date_creation) || 0), 0),
    [encours],
  );

  const demandesVues = useMemo(
    () => demandes.filter(filtreTexte(q, ['nom_client', 'nom_produit', 'telephone_client', (x) => x.id_commande])),
    [demandes, q],
  );
  const encoursVus = useMemo(
    () => encours
      .filter(filtreTexte(q, ['nom_client', 'produit_neuf', 'produit_ancien', 'livreur', 'telephone_client', (x) => x.id_commande]))
      .filter((x) => !livreur || String(x.livreur) === livreur),
    [encours, q, livreur],
  );
  const histoVu = useMemo(
    () => histo.filter(filtreTexte(q, ['nom_client', 'produit_neuf', 'produit_ancien', 'livreur', (x) => x.id_commande])),
    [histo, q],
  );

  const traiter = (d) => {
    // On rejoint le flux existant, qui sait présélectionner la bonne ligne.
    navigate(`/echangeForm?commande=${d.id_commande}&variante=${d.id_varianteProduit || ''}`);
  };

  const confirmerAnnulation = async () => {
    if (!motif.trim()) { toast.error('Le motif est obligatoire.'); return; }
    const d = annulCible;
    if (!d) return;
    try {
      setAnnulEnCours(true);
      await axios.put(`${DOMAIN}/api/livraison/echanges-demandes/${d.id_detail_livraison}/annuler`, { motif });
      toast.success('Demande annulée.');
      setDemandes((prev) => prev.filter((x) => x.id_detail_livraison !== d.id_detail_livraison));
      setAnnulCible(null); setMotif('');
    } catch (e) {
      toast.error(e?.response?.data?.error || "Annulation impossible.");
    } finally {
      setAnnulEnCours(false);
    }
  };

  const relancer = async (e) => {
    try {
      setRelanceId(e.id_detail_livraison);
      const { data } = await axios.post(`${DOMAIN}/api/livraison/echanges-a-livrer/${e.id_detail_livraison}/relancer`);
      // ON NE DIT PAS « ENVOYÉE » SI RIEN N'EST PARTI. Le serveur renvoie le
      // nombre d'appareils touchés : sans téléphone enregistré, la notification
      // n'existe pas et la secrétaire doit le savoir pour appeler.
      if (data?.livre) {
        toast.success(`Relance envoyée à ${data.livreur || 'le livreur'}.`);
      } else {
        toast.warning(
          `${data?.livreur || 'Ce livreur'} n’a aucun téléphone enregistré : la relance n’a pas pu lui être envoyée. Prévenez-le autrement.`,
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Relance impossible.');
    } finally {
      setRelanceId(null);
    }
  };

  /** Corps d'un onglet : la panne passe AVANT le vide, jamais à sa place. */
  const contenu = (erreur, liste, vide, rendu) => {
    if (erreur) return <NdoeError title="Liste non chargée" text={erreur} onRetry={charger} />;
    if (!liste.length) return vide;
    return <ul className="ce-liste">{liste.map(rendu)}</ul>;
  };

  return (
    <NdoePage>
      <NdoePageHeader
        title="Centre des échanges"
        subtitle="Les demandes, les missions en cours et ce qui s'est dénoué"
        actions={
          <NdoeActionButton variant="secondary" onClick={charger}>Actualiser</NdoeActionButton>
        }
      />

      {loading ? <NdoeLoading rows={6} /> : (
        <>
          <NdoeStats>
            {/* Un compteur est un CHIFFRE : on n'affiche pas « 0 » quand on n'a
                rien reçu. Un tiret dit « je ne sais pas ». */}
            <NdoeStatCard label="À traiter" value={errDemandes ? '—' : demandes.length} />
            <NdoeStatCard label="En cours chez un livreur" value={errEncours ? '—' : encours.length} gold />
            <NdoeStatCard
              label="Le plus ancien en cours"
              value={errEncours ? '—' : (encours.length ? `${plusVieux} j` : '—')}
            />
          </NdoeStats>

          <NdoeRecherche
            q={q}
            onQ={setQ}
            placeholder="Cliente, téléphone, n° de commande, modèle, livreur…"
            facettes={onglet === 'encours' ? [{
              cle: 'livreur',
              placeholder: 'Livreur',
              options: optionsDe(encours, 'livreur'),
              valeur: livreur,
              onChange: setLivreur,
              largeur: 160,
            }] : []}
            actifs={compterActifs(q, onglet === 'encours' ? livreur : null)}
            onEffacer={() => { setQ(''); setLivreur(undefined); }}
          />

          <Tabs
            activeKey={onglet}
            onChange={setOnglet}
            items={[
              {
                key: 'demandes',
                label: `À traiter${!errDemandes && demandes.length ? ` (${demandes.length})` : ''}`,
                children: contenu(
                  errDemandes,
                  demandesVues,
                  <NdoeEmpty icon="🔁" title="Aucune demande" text="Rien en attente de traitement." />,
                  (d) => (
                    <li key={d.id_detail_livraison || `${d.id_commande}-${d.id_varianteProduit}`} className="ce-carte">
                      <NdoeImage src={d.img} alt={d.nom_produit || 'Paire'} size="md" />
                      <div className="ce-carte__corps">
                        <div className="ce-carte__tete">
                          <b>#{d.id_commande}</b>
                          <span>{d.nom_client || 'Cliente'}</span>
                        </div>
                        <small>{paireLisible(d.nom_produit, d.taille)}</small>
                        {d.date_creation && <small className="ce-age">Demandé il y a {jours(d.date_creation)} j</small>}
                      </div>
                      <div className="ce-carte__actions">
                        <LienWhatsApp tel={d.telephone_client} nom={d.nom_client} />
                        {/* Annuler une demande signalée par erreur : réservé à
                            l'admin côté serveur, motif obligatoire et tracé. */}
                        {estAdmin && d.id_detail_livraison && (
                          <NdoeActionButton size="sm" variant="ghost" onClick={() => { setAnnulCible(d); setMotif(''); }}>
                            Annuler
                          </NdoeActionButton>
                        )}
                        <NdoeActionButton size="sm" variant="primary" onClick={() => traiter(d)}>Traiter</NdoeActionButton>
                      </div>
                    </li>
                  ),
                ),
              },
              {
                key: 'encours',
                label: `En cours${!errEncours && encours.length ? ` (${encours.length})` : ''}`,
                children: contenu(
                  errEncours,
                  encoursVus,
                  <NdoeEmpty icon="🚚" title="Aucun échange en cours" text="Rien n'est actuellement chez un livreur." />,
                  (e) => {
                    const age = jours(e.date_creation);
                    return (
                      <li key={e.id_detail_livraison} className={`ce-carte${age >= 3 ? ' is-vieux' : ''}`}>
                        <NdoeImage src={e.img_neuf} alt={e.produit_neuf || 'Paire'} size="md" />
                        <div className="ce-carte__corps">
                          <div className="ce-carte__tete">
                            <b>#{e.id_commande}</b>
                            <span>{e.nom_client || 'Cliente'}</span>
                            {e.avarie_ancienne ? <span className="ce-tag ce-tag--avarie">Avarie</span> : null}
                          </div>
                          {/* L'échange se lit d'un coup d'œil : ce qu'on reprend → ce qu'on remet. */}
                          <small className="ce-swap">
                            <span className="ce-swap__old">{paireLisible(e.produit_ancien, e.taille_ancien) || 'Ancienne paire'}</span>
                            <span className="ce-swap__fleche" aria-hidden="true">→</span>
                            <span className="ce-swap__new">{paireLisible(e.produit_neuf, e.taille_neuf)}</span>
                          </small>
                          {/*
                              L'ARGENT DE L'ÉCHANGE EN COURS (31/08/2026).

                              Cet onglet était muet sur les montants — pour tout le
                              monde, admin compris : le prix retenu est écrit à
                              l'initiation mais n'était jamais relu. Entre
                              l'initiation et l'exécution, on ne savait pas ce qui
                              restait à encaisser.

                              `ecart` est calculé par le serveur, et vaut null quand
                              l'ancien prix est inconnu — on écrit alors « ancien prix
                              inconnu » plutôt qu'un montant. Un chiffre inventé dans
                              une colonne d'argent est pire que pas de chiffre.

                              La quantité n'est PAS multipliée : le prix stocké est
                              tantôt unitaire, tantôt total selon l'écran qui a créé
                              l'échange. On l'affiche tel quel, avec « ×N » à côté
                              quand il y en a plusieurs.
                          */}
                          {peutVoirMontants && e.prix_neuve != null && (
                            <small className="ce-argent">
                              <span>Nouvelle {e.prix_neuve} $</span>
                              {e.qte > 1 ? <span className="ce-argent__qte">×{e.qte}</span> : null}
                              {e.prix_ancienne != null
                                ? <span>· {e.ancienne_vendue ? 'elle payait' : 'ancienne'} {e.prix_ancienne} $</span>
                                : <span className="ce-argent__flou">· ancien prix inconnu</span>}
                              {e.ecart != null && e.ecart !== 0 && (
                                <b className={e.ecart > 0 ? 'ce-argent--encaisser' : 'ce-argent--rembourser'}>
                                  · {Math.abs(e.ecart)} $ {e.ecart > 0 ? 'à encaisser' : 'à rembourser'}
                                </b>
                              )}
                              {e.ecart === 0 && <span>· aucun écart</span>}
                            </small>
                          )}
                          <small className="ce-age">
                            {e.livreur ? `Chez ${e.livreur}` : 'Livreur non assigné'}
                            {age !== null ? ` · depuis ${age} j` : ''}
                          </small>
                        </div>
                        <div className="ce-carte__actions">
                          <LienWhatsApp tel={e.telephone_client} nom={e.nom_client} />
                          {/* Relance : proposée seulement quand il y a quelqu'un à
                              relancer, et seulement au bout de 2 jours — avant, ce
                              n'est pas du retard, c'est le délai normal. */}
                          {peutRelancer && e.id_livreur && age >= 2 && (
                            <NdoeActionButton
                              size="sm"
                              variant="secondary"
                              loading={relanceId === e.id_detail_livraison}
                              onClick={() => relancer(e)}
                            >
                              Relancer
                            </NdoeActionButton>
                          )}
                        </div>
                      </li>
                    );
                  },
                ),
              },
              {
                key: 'histo',
                label: 'Historique',
                children: contenu(
                  errHisto,
                  histoVu,
                  <NdoeEmpty icon="🗂️" title="Rien dans l'historique" text="Aucun échange dénoué pour l'instant." />,
                  (h) => (
                    <li key={h.id_detail_livraison} className="ce-carte ce-carte--histo">
                      <NdoeImage src={h.img_neuf} alt={h.produit_neuf || 'Paire'} size="md" />
                      <div className="ce-carte__corps">
                        <div className="ce-carte__tete">
                          <b>#{h.id_commande}</b>
                          <span>{h.nom_client || 'Cliente'}</span>
                          <span className={`ce-tag ${h.statut_livraison === 'ECHANGE_ANNULE' ? 'ce-tag--annule' : 'ce-tag--fait'}`}>
                            {h.statut_livraison === 'ECHANGE_ANNULE' ? 'Annulé' : 'Terminé'}
                          </span>
                        </div>
                        <small className="ce-swap">
                          <span className="ce-swap__old">{paireLisible(h.produit_ancien, h.taille_ancien) || 'Ancienne paire'}</span>
                          <span className="ce-swap__fleche" aria-hidden="true">→</span>
                          <span className="ce-swap__new">{paireLisible(h.produit_neuf, h.taille_neuf)}</span>
                        </small>
                        {/* L'écart de prix ne se relit d'aucune table : il vit dans le
                            journal. C'est pourtant la seule chose qui reste à faire
                            après coup — on la remonte ici. */}
                        {h.ecart_prix && <small className="ce-ecart">{h.ecart_prix}</small>}
                        <small className="ce-age">
                          {h.livreur ? `${h.livreur} · ` : ''}
                          {h.date_resultat ? new Date(h.date_resultat).toLocaleDateString('fr-FR') : ''}
                        </small>
                      </div>
                      {/* Recontacter APRÈS coup : un écart de prix à régler, une
                          paire à reprendre. */}
                      <div className="ce-carte__actions">
                        <LienWhatsApp tel={h.telephone_client} nom={h.nom_client} />
                      </div>
                    </li>
                  ),
                ),
              },
            ]}
          />
        </>
      )}

      <Modal
        title="Annuler la demande d'échange"
        open={!!annulCible}
        onCancel={() => { setAnnulCible(null); setMotif(''); }}
        onOk={confirmerAnnulation}
        okText="Annuler la demande"
        cancelText="Revenir"
        confirmLoading={annulEnCours}
        destroyOnClose
      >
        <p>
          Annuler la demande de {annulCible?.nom_client || 'cette cliente'}
          {annulCible?.id_commande ? ` (commande #${annulCible.id_commande})` : ''} ?
        </p>
        {/* Le motif n'est pas une formalité : il apparaît au Journal des
            suppressions, c'est ce qui permet de comprendre l'annulation
            plus tard. */}
        <Input.TextArea
          rows={3}
          value={motif}
          onChange={(ev) => setMotif(ev.target.value)}
          placeholder="Motif (obligatoire) — par exemple : signalée par erreur"
        />
      </Modal>
    </NdoePage>
  );
};

export default CentreEchanges;
